import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, Search, Bot, Lock, MessageSquare, Plane, ShieldCheck, Loader2, Sparkles,
  CheckSquare, Square, UsersRound, UserPlus, X,
} from "lucide-react";

const ROLE_COLORS = {
  admin: "bg-fuchsia-500/15 text-fuchsia-500 border-fuchsia-500/40",
  member: "bg-sky-500/15 text-sky-500 border-sky-500/40",
  viewer: "bg-slate-500/15 text-slate-500 border-slate-500/40",
};

function initials(name) {
  return (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function PeopleDirectory() {
  const { user, isAdmin, workspace, refresh } = useAuth();
  const nav = useNavigate();
  const [people, setPeople] = useState([]);
  const [mine, setMine] = useState(null); // bot-access/mine
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reqTarget, setReqTarget] = useState(null); // {user_id, name}
  const [reqReason, setReqReason] = useState("");
  const [openBotAccess, setOpenBotAccess] = useState(!!workspace?.open_bot_access);
  const [toggling, setToggling] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkGrant, setShowBulkGrant] = useState(false);
  const [bulkGrantee, setBulkGrantee] = useState("");
  const [bulkNote, setBulkNote] = useState("");

  useEffect(() => { setOpenBotAccess(!!workspace?.open_bot_access); }, [workspace?.open_bot_access]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        api.get("/memory/people"),
        api.get("/bot-access/mine"),
      ]);
      setPeople(p.data || []);
      setMine(m.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grantedIds = useMemo(() => {
    if (!mine) return new Set();
    return new Set(mine.held.map((g) => g.target_user_id));
  }, [mine]);
  const pendingIds = useMemo(() => {
    if (!mine) return new Set();
    return new Set(mine.pending_outgoing.map((g) => g.target_user_id));
  }, [mine]);

  const filtered = useMemo(() => {
    if (!q.trim()) return people;
    const s = q.trim().toLowerCase();
    return people.filter((p) => (p.name || "").toLowerCase().includes(s)
      || (p.email || "").toLowerCase().includes(s)
      || (p.aliases || []).some((a) => a.toLowerCase().includes(s)));
  }, [people, q]);

  const canQuery = (p) => {
    if (p.user_id === user?.id) return true;
    if (openBotAccess) return true;
    if (isAdmin) return true;
    return grantedIds.has(p.user_id);
  };

  const queryBot = (p) => {
    const alias = (p.aliases && p.aliases[0]) || (p.name || "").split(" ")[0].toLowerCase();
    nav(`/app/chat?prefill=${encodeURIComponent(`/${alias} `)}`);
  };

  const request = async () => {
    if (!reqTarget) return;
    setBusy(true);
    try {
      const r = await api.post("/bot-access/request", {
        target_user_id: reqTarget.user_id, reason: reqReason,
      });
      if (r.data.already) toast.info("Request already exists");
      else toast.success(`Request sent to ${reqTarget.name}`);
      setReqTarget(null); setReqReason("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleOpen = async () => {
    if (!isAdmin) return;
    const desired = !openBotAccess;
    if (desired && !window.confirm("Turn ON open bot access? Every member will be able to query every colleague's bot in this workspace without individual grants.")) return;
    setToggling(true);
    try {
      await api.patch(`/workspaces/${workspace.id}/settings`, { open_bot_access: desired });
      setOpenBotAccess(desired);
      await refresh();
      toast.success(desired ? "Open bot access ON — everyone can query everyone" : "Open bot access OFF");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setToggling(false); }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="people-skeleton" role="status" aria-label="Loading directory">
        <header className="space-y-2">
          <div className="h-3 w-16 rounded bg-muted animate-pulse" />
          <div className="h-8 w-64 rounded bg-muted animate-pulse" />
          <div className="h-3 w-96 rounded bg-muted/70 animate-pulse" />
        </header>
        <div className="h-10 w-full max-w-md rounded bg-muted/60 animate-pulse" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border border-border/60 rounded-lg p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-muted animate-pulse" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-3.5 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-2.5 w-1/2 rounded bg-muted/70 animate-pulse" />
                  <div className="h-2.5 w-1/3 rounded bg-muted/70 animate-pulse" />
                </div>
              </div>
              <div className="h-2.5 w-2/5 rounded bg-muted/70 animate-pulse" />
              <div className="flex gap-2">
                <div className="h-8 flex-1 rounded-full bg-muted animate-pulse" />
                <div className="h-8 w-16 rounded bg-muted/70 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="people-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-sky-500">People</p>
          <h1 className="font-display text-3xl mt-1 flex items-center gap-2">
            Workspace members <span className="text-sm text-muted-foreground font-normal">· {people.length}</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Query anyone's bot with <code>/alias</code> — subject to their access rules. Admins can query any bot; teammates need a grant.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="open-bot-access-toggle"
            onClick={toggleOpen}
            disabled={toggling}
            className={`flex items-center gap-3 border rounded-full px-4 py-2 text-sm transition-colors ${
              openBotAccess
                ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-500"
                : "border-border/60 hover:bg-secondary/40"
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            <span className="flex flex-col items-start leading-tight">
              <span className="font-medium">Open bot access {openBotAccess ? "ON" : "OFF"}</span>
              <span className="text-[10px] opacity-70">
                {openBotAccess ? "Everyone can query everyone" : "Individual grants required"}
              </span>
            </span>
            {toggling && <Loader2 className="w-3 h-3 animate-spin" />}
          </button>
        )}
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="people-search"
            placeholder="Search by name, alias, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        {isAdmin && (
          <>
            <Button
              variant={bulkMode ? "default" : "outline"}
              size="sm"
              onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
              className="rounded-full"
              data-testid="people-bulk-toggle"
            >
              <UsersRound className="w-3.5 h-3.5 mr-1" />
              {bulkMode ? "Cancel selection" : "Bulk grant"}
            </Button>
            {bulkMode && selectedIds.size > 0 && (
              <Button size="sm" onClick={() => setShowBulkGrant(true)}
                      className="rounded-full"
                      data-testid="people-bulk-grant-btn">
                Grant access to {selectedIds.size} {selectedIds.size === 1 ? "person" : "people"} →
              </Button>
            )}
          </>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="people-grid">
        {people.length === 0 ? (
          <div
            className="col-span-full flex flex-col items-center text-center gap-3 py-16 px-6 border border-dashed border-border/60 rounded-lg"
            data-testid="people-empty-state"
          >
            <div className="empty-icon w-12 h-12 rounded-full bg-sky-500/10 border border-sky-500/30 flex items-center justify-center text-sky-500">
              <UserPlus className="w-5 h-5 text-sky-500" />
            </div>
            <div className="max-w-sm">
              <p className="font-display text-lg">No teammates yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Invite the people you work with — each one gets their own memory that everyone can query with <code>/alias</code>.
              </p>
            </div>
            {isAdmin && (
              <Link to="/app/members">
                <Button size="sm" className="rounded-full mt-2" data-testid="people-empty-invite-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Invite teammates
                </Button>
              </Link>
            )}
          </div>
        ) : filtered.length === 0 && (
          <div
            className="col-span-full flex flex-col items-center text-center gap-3 py-12 px-6 border border-dashed border-border/60 rounded-lg"
            data-testid="people-empty-search"
          >
            <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center">
              <Search className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="max-w-sm">
              <p className="font-display text-base">No matches for "{q}"</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try a shorter search, or clear the filter to see all {people.length} teammates.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQ("")}
              className="rounded-full mt-1"
              data-testid="people-empty-clear-btn"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Clear search
            </Button>
          </div>
        )}
        {filtered.map((p) => {
          const isSelf = p.user_id === user?.id;
          const allowed = canQuery(p);
          const pending = pendingIds.has(p.user_id);
          const alias = (p.aliases && p.aliases[0]) || "—";
          return (
            <div
              key={p.user_id}
              data-testid={`people-card-${p.user_id}`}
              className="border border-border/60 rounded-lg p-4 flex flex-col gap-3 hover:border-border transition-colors"
            >
              <div className="flex items-start gap-3">
                {bulkMode && !isSelf && (
                  <button
                    type="button"
                    data-testid={`people-select-${p.user_id}`}
                    onClick={() => setSelectedIds((s) => {
                      const n = new Set(s);
                      n.has(p.user_id) ? n.delete(p.user_id) : n.add(p.user_id);
                      return n;
                    })}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {selectedIds.has(p.user_id)
                      ? <CheckSquare className="w-4 h-4 text-sky-500" />
                      : <Square className="w-4 h-4" />}
                  </button>
                )}
                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-semibold ${ROLE_COLORS[p.role] || "bg-secondary text-foreground"}`}>
                  {initials(p.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{p.name || p.email}{isSelf && <span className="text-xs text-muted-foreground ml-1">(you)</span>}</p>
                    <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border ${ROLE_COLORS[p.role] || ""}`}>
                      {p.role}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{p.email}</p>
                  <p className="text-[11px] text-muted-foreground font-mono mt-0.5">/{alias}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="w-3 h-3" />
                <span>{p.memory_count || 0} memory entries</span>
                {p.departure_date && (
                  <span className="ml-auto flex items-center gap-1 text-amber-500">
                    <Plane className="w-3 h-3" />
                    departing
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                {allowed ? (
                  <Button
                    size="sm"
                    onClick={() => queryBot(p)}
                    className="rounded-full flex-1"
                    data-testid={`people-query-${p.user_id}`}
                  >
                    <Bot className="w-3.5 h-3.5 mr-1" /> Query bot
                  </Button>
                ) : pending ? (
                  <Button size="sm" variant="outline" disabled className="rounded-full flex-1"
                          data-testid={`people-pending-${p.user_id}`}>
                    <Loader2 className="w-3.5 h-3.5 mr-1" /> Request pending
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReqTarget({ user_id: p.user_id, name: p.name || p.email })}
                    className="rounded-full flex-1"
                    data-testid={`people-request-${p.user_id}`}
                  >
                    <Lock className="w-3.5 h-3.5 mr-1" /> Request access
                  </Button>
                )}
                <Link
                  to={`/app/memory?person=${p.user_id}`}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center px-2"
                  data-testid={`people-memory-${p.user_id}`}
                >
                  Memory →
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={showBulkGrant} onOpenChange={(o) => !o && setShowBulkGrant(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk grant bot access</DialogTitle>
            <DialogDescription>
              Grant one teammate the ability to query the bots of the {selectedIds.size} selected {selectedIds.size === 1 ? "person" : "people"}.
              This is instant — no approval needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Grantee — who gets the access?</label>
              <select
                data-testid="people-bulk-grantee-select"
                value={bulkGrantee}
                onChange={(e) => setBulkGrantee(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded border border-border/60 bg-background text-sm"
              >
                <option value="">Pick a teammate…</option>
                {people
                  .filter((p) => !selectedIds.has(p.user_id))
                  .map((p) => (
                    <option key={p.user_id} value={p.user_id}>
                      {p.name || p.email} ({p.role})
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Note (optional — visible in audit log)</label>
              <Input
                data-testid="people-bulk-note"
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder="e.g. new manager onboarding"
                className="mt-1"
              />
            </div>
            <div className="text-xs text-muted-foreground bg-secondary/40 rounded p-3">
              Targets ({selectedIds.size}):{" "}
              <span className="text-foreground">
                {people.filter((p) => selectedIds.has(p.user_id)).map((p) => p.name || p.email).join(", ")}
              </span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setShowBulkGrant(false)}>Cancel</Button>
              <Button
                data-testid="people-bulk-grant-submit"
                disabled={!bulkGrantee || busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await api.post("/bot-access/bulk-grant", {
                      grantee_user_ids: [bulkGrantee],
                      target_user_ids: Array.from(selectedIds),
                      note: bulkNote || null,
                    });
                    toast.success(`Granted — ${r.data.created} new, ${r.data.reactivated} reactivated, ${r.data.skipped} skipped`);
                    setShowBulkGrant(false); setBulkMode(false); setSelectedIds(new Set());
                    setBulkGrantee(""); setBulkNote("");
                    await load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e?.response?.data?.detail));
                  } finally { setBusy(false); }
                }}
              >
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Grant
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reqTarget} onOpenChange={(o) => !o && setReqTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request access to {reqTarget?.name}'s bot</DialogTitle>
            <DialogDescription>
              They'll get a notification and can approve or decline. If they approve, you'll be able to query with /{(reqTarget?.name || "").split(" ")[0].toLowerCase()}.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reqReason}
            onChange={(e) => setReqReason(e.target.value)}
            placeholder="Optional reason — e.g. 'Need context on the APAC deal handover'"
            rows={3}
            data-testid="people-request-reason"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setReqTarget(null)}>Cancel</Button>
            <Button onClick={request} disabled={busy} data-testid="people-request-send">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send request
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
